/**
 * Database Rides Panel Component
 * 
 * Right panel displaying rides from UAT/Production database.
 * Automatically fetches and displays rides when prerequisites are met.
 * Allows users to view, search, and select rides for download operations.
 * 
 * @module DatabaseRidesPanel
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Skeleton,
  TextField,
  LinearProgress,
  Tooltip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  Checkbox,
  ListItemText,
} from '@mui/material';

import { useTranslation } from 'react-i18next';
import {
  fetchRides,
  fetchRidesDetailedBatch,
  fetchNetworkSettings,
  type UATNetworkSettingsResponse,
  type Environment,
  type UATRideEntry,
  type UATRideDetailedEntry,
} from '../../../../services/fixedRouteUatClient';
import { RideCard } from './RideCard';
import { RideSearchBar } from './RideSearchBar';
import { useDebounce } from '../../../../hooks/useDebounce';
import { getEnvironmentDisplayName } from '../EnvironmentSelector';
import { formatServiceDateLabel } from '../../../../utils/dateTimeUtils';

import { Icon } from '@liftango/ui-icons';
/**
 * Props for DatabaseRidesPanel component
 */
export interface DatabaseRidesPanelProps {
  /** JWT token for API authentication */
  jwtToken: string;
  /** Selected environment (UAT or Production) */
  environment: Environment;
  /** Start date for ride operations (YYYY-MM-DD) */
  startDate: string;
  /** End date for ride operations (YYYY-MM-DD) */
  endDate: string;
  /** Callback when start date changes */
  onStartDateChange?: (date: string) => void;
  /** Callback when end date changes */
  onEndDateChange?: (date: string) => void;
  /** Timezone for ride operations */
  timezone: string;
  /** Selected product ID */
  selectedProductId: string;
  /** Selected network ID */
  selectedNetworkId: number | null;
  /** Network settings response (for timezone) */
  networkSettings: UATNetworkSettingsResponse | null;
  /** Whether download acknowledgment is checked */
  downloadAcknowledged: boolean;
  /** Selected ride IDs */
  selectedRideIds: Set<string>;
  /** Callback when selection changes */
  onSelectionChange: (selectedIds: Set<string>) => void;
  /** Callback to update ride count in parent header */
  onRideCountChange?: (count: number, filteredCount?: number) => void;
  /** Callback to trigger refresh (called after upload) */
  onRefresh?: () => void;
  /** Callback to register refresh function */
  onRefreshRegister?: (refreshFn: () => void) => void;
  /** Callback when rides are loaded/updated */
  onRidesChange?: (rides: (UATRideEntry | UATRideDetailedEntry)[]) => void;
}

/**
 * DatabaseRidesPanel Component
 * 
 * Displays rides from database with automatic fetching, search, and selection functionality.
 */
export const DatabaseRidesPanel: React.FC<DatabaseRidesPanelProps> = ({
  jwtToken,
  environment,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  timezone,
  selectedProductId,
  selectedNetworkId,
  networkSettings,
  downloadAcknowledged,
  selectedRideIds,
  onSelectionChange,
  onRideCountChange,
  onRefresh,
  onRefreshRegister,
  onRidesChange,
}) => {
  const { t } = useTranslation('mapTransfer');
  const [rides, setRides] = useState<(UATRideEntry | UATRideDetailedEntry)[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const [expandedRideIds, setExpandedRideIds] = useState<Set<string>>(new Set());
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  
  // Loading state details
  const [loadingStage, setLoadingStage] = useState<'idle' | 'fetching-list' | 'fetching-details'>('idle');
  const [loadingProgress, setLoadingProgress] = useState<{ current: number; total: number } | null>(null);
  const [loadingMessage, setLoadingMessage] = useState<string>('');
  
  // Ref to track the last reported progress to ensure it only increases
  const lastProgressRef = React.useRef<number>(0);
  
  // Local date state for debouncing
  const [localStartDate, setLocalStartDate] = useState(startDate);
  const [localEndDate, setLocalEndDate] = useState(endDate);
  const debouncedStartDate = useDebounce(localStartDate, 500);
  const debouncedEndDate = useDebounce(localEndDate, 500);
  
  // Date validation
  const [dateError, setDateError] = useState<string | null>(null);

  // Sync local date state with props
  useEffect(() => {
    setLocalStartDate(startDate);
    setLocalEndDate(endDate);
  }, [startDate, endDate]);

  // Validate dates. `<input type="date">` guarantees YYYY-MM-DD format, so
  // string comparison is timezone-safe and avoids the `new Date("YYYY-MM-DD")`
  // UTC-midnight pitfall.
  useEffect(() => {
    if (!localStartDate || !localEndDate) {
      setDateError(null);
      return;
    }

    const dateOnly = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateOnly.test(localStartDate) || !dateOnly.test(localEndDate)) {
      setDateError(t('rides.panel.dateErrorInvalid'));
      return;
    }

    if (localStartDate > localEndDate) {
      setDateError(t('rides.panel.dateErrorStartAfterEndAlt'));
      return;
    }

    setDateError(null);
  }, [localStartDate, localEndDate, t]);

  // Update parent when debounced dates change
  useEffect(() => {
    if (dateError) {
      return; // Don't update if there's a validation error
    }

    if (debouncedStartDate !== startDate && onStartDateChange) {
      onStartDateChange(debouncedStartDate);
      // Clear selection when date range changes
      onSelectionChange(new Set());
    }
  }, [debouncedStartDate, startDate, onStartDateChange, dateError, onSelectionChange]);

  useEffect(() => {
    if (dateError) {
      return; // Don't update if there's a validation error
    }

    if (debouncedEndDate !== endDate && onEndDateChange) {
      onEndDateChange(debouncedEndDate);
      // Clear selection when date range changes
      onSelectionChange(new Set());
    }
  }, [debouncedEndDate, endDate, onEndDateChange, dateError, onSelectionChange]);

  // Calculate effective timezone from network settings
  const effectiveTimezone = useMemo(() => {
    return networkSettings?.data?.networks?.[0]?.networkSettings?.timezone || timezone;
  }, [networkSettings, timezone]);

  // Ref to prevent concurrent fetches
  const isFetchingRef = React.useRef(false);
  // Ref to track last fetched parameters to prevent unnecessary refetches
  const lastFetchParamsRef = React.useRef<{
    jwtToken: string;
    selectedProductId: string;
    environment: Environment;
    startDate: string;
    endDate: string;
    downloadAcknowledged: boolean;
  } | null>(null);
  // Fetch rides function
  const fetchRidesData = useCallback(async () => {
    if (
      !jwtToken.trim() ||
      !selectedProductId ||
      !downloadAcknowledged ||
      !startDate ||
      !endDate
    ) {
      console.log('[DatabaseRidesPanel] Missing prerequisites, skipping fetch');
      return;
    }

    // Prevent concurrent fetches
    if (isFetchingRef.current) {
      console.log('[DatabaseRidesPanel] Fetch already in progress, skipping...');
      return;
    }

    // Check if parameters have actually changed (only skip if ALL parameters are identical)
    const currentParams = {
      jwtToken,
      selectedProductId,
      environment,
      startDate,
      endDate,
      downloadAcknowledged,
    };

    // If we have previous params, check if anything changed
    // Only skip if parameters are EXACTLY the same (prevents infinite loops)
    if (lastFetchParamsRef.current) {
      const paramsChanged = 
        lastFetchParamsRef.current.jwtToken !== currentParams.jwtToken ||
        lastFetchParamsRef.current.selectedProductId !== currentParams.selectedProductId ||
        lastFetchParamsRef.current.environment !== currentParams.environment ||
        lastFetchParamsRef.current.startDate !== currentParams.startDate ||
        lastFetchParamsRef.current.endDate !== currentParams.endDate ||
        lastFetchParamsRef.current.downloadAcknowledged !== currentParams.downloadAcknowledged;

      // Only skip if parameters are EXACTLY the same (prevents infinite loops)
      if (!paramsChanged) {
        console.log('[DatabaseRidesPanel] Parameters unchanged, skipping fetch...', {
          previous: lastFetchParamsRef.current,
          current: currentParams,
        });
        return;
      } else {
        console.log('[DatabaseRidesPanel] Parameters changed, fetching...', {
          previous: lastFetchParamsRef.current,
          current: currentParams,
        });
      }
    } else {
      console.log('[DatabaseRidesPanel] Initial fetch, no previous params');
    }

    // Store current params before fetching
    lastFetchParamsRef.current = currentParams;
    isFetchingRef.current = true;
    setLoading(true);
    setError(null);
    setLoadingStage('fetching-list');
    setLoadingProgress(null);
    setLoadingMessage(t('rides.panel.loading.fetchingRange', {
      start: formatServiceDateLabel(startDate),
      end: formatServiceDateLabel(endDate),
    }));
    lastProgressRef.current = 0; // Reset progress tracker at start

    try {
      // Helper to check if a ride is within the date range
      const isRideInDateRange = (ride: UATRideEntry | UATRideDetailedEntry): boolean => {
        const rideTime = ride.departureTime || ride.time;
        if (!rideTime) return false; // Exclude rides without time
        
        try {
          // Parse the ride time and extract just the date part (YYYY-MM-DD)
          const rideDate = new Date(rideTime);
          if (isNaN(rideDate.getTime())) return false;
          
          // Get date in YYYY-MM-DD format using UTC to avoid timezone issues
          const rideYear = rideDate.getUTCFullYear();
          const rideMonth = String(rideDate.getUTCMonth() + 1).padStart(2, '0');
          const rideDay = String(rideDate.getUTCDate()).padStart(2, '0');
          const rideDateOnly = `${rideYear}-${rideMonth}-${rideDay}`;
          
          // Compare date strings directly
          return rideDateOnly >= startDate && rideDateOnly <= endDate;
        } catch (error) {
          console.warn(`[DatabaseRidesPanel] Error parsing ride date for ${ride.id}:`, error);
          return false; // Exclude if we can't parse the date
        }
      };

      // Helper to extract date from ride
      const getRideDate = (ride: UATRideEntry | UATRideDetailedEntry): string | null => {
        const rideTime = ride.departureTime || ride.time;
        if (!rideTime) return null;
        try {
          const rideDate = new Date(rideTime);
          if (isNaN(rideDate.getTime())) return null;
          return rideDate.toISOString().split('T')[0];
        } catch {
          return null;
        }
      };

      // Determine sort order based on date range
      // If end date is in the past, use ASC to get older rides first
      // Otherwise use DESC to get most recent rides first
      const today = new Date().toISOString().split('T')[0];
      const isHistoricalData = endDate < today;
      const sortOrder: 'ASC' | 'DESC' = isHistoricalData ? 'ASC' : 'DESC';
      
      // IMPORTANT: The API does NOT filter by startDate/endDate parameters
      // It returns all rides regardless of the date parameters we send
      // We must fetch pages and filter client-side, stopping when we've left the date range
      
      const pageLimit = 100; // Rides per page
      const MAX_PAGES_TO_FETCH = isHistoricalData ? 50 : 20; // More pages for historical dates
      
      const allRideEntries: UATRideEntry[] = [];
      let shouldContinueFetching = true;
      let pagesFetched = 0;
      
      setLoadingMessage(t('rides.panel.loading.fetchingRangeWithKind', {
        start: formatServiceDateLabel(startDate),
        end: formatServiceDateLabel(endDate),
        kind: isHistoricalData ? t('rides.panel.loading.kindHistorical') : t('rides.panel.loading.kindCurrent'),
      }));
      
      // Intelligent stopping: track consecutive pages with no rides in range
      const MAX_CONSECUTIVE_EMPTY_PAGES = 3;
      let consecutiveEmptyPages = 0;
      
      // Fetch pages one at a time, stopping when we've left the date range
      for (let page = 0; page < MAX_PAGES_TO_FETCH && shouldContinueFetching; page++) {
        const pageOffset = page * pageLimit;
        
        // Fetch page (API ignores date parameters, so we don't send them)
        const pageResponse = await fetchRides(
          jwtToken,
          selectedProductId,
          undefined, // API ignores date parameters
          undefined, // API ignores date parameters
          pageOffset,
          pageLimit,
          'departureTime',
          sortOrder,
          environment
        );
        
        if (!pageResponse?.data?.entries || pageResponse.data.entries.length === 0) {
          // No more rides available, stop
          shouldContinueFetching = false;
          console.log(`[DatabaseRidesPanel] Stopping fetch at page ${page + 1} - no more rides available`);
          break;
        }
        
        const pageRides = pageResponse.data.entries;
        pagesFetched++;
        
        // Filter rides in date range
        const ridesInRange = pageRides.filter(isRideInDateRange);
        allRideEntries.push(...ridesInRange);
        
        // Update loading message
        setLoadingMessage(t('rides.panel.loading.fetchedPage', { count: ridesInRange.length, page: page + 1, rangeCount: ridesInRange.length, pageCount: pageRides.length }));
        
        // Intelligent stopping: if no rides in range on this page, increment counter
        if (ridesInRange.length === 0) {
          consecutiveEmptyPages++;
          console.log(`[DatabaseRidesPanel] Page ${page + 1} has no rides in range (${consecutiveEmptyPages} consecutive empty pages)`);
          
          // Stop if we've had too many consecutive empty pages
          if (consecutiveEmptyPages >= MAX_CONSECUTIVE_EMPTY_PAGES) {
            shouldContinueFetching = false;
            console.log(`[DatabaseRidesPanel] Stopping fetch after ${MAX_CONSECUTIVE_EMPTY_PAGES} consecutive pages with no rides in range`);
            break;
          }
        } else {
          // Reset counter if we found rides in range
          consecutiveEmptyPages = 0;
        }
        
        // Determine if we should stop fetching based on sort order
        if (sortOrder === 'DESC') {
          // DESC: newest first - we're going backwards in time
          // Stop if ALL rides on this page are before startDate (we've clearly passed the range)
          const allBeforeStartDate = pageRides.every(ride => {
            const rideDate = getRideDate(ride);
            return rideDate !== null && rideDate < startDate;
          });
          
          if (allBeforeStartDate) {
            shouldContinueFetching = false;
            console.log(`[DatabaseRidesPanel] Stopping fetch at page ${page + 1} - all rides are before start date`);
          }
        } else {
          // ASC: oldest first - we're going forward in time (for historical dates)
          // Stop if ALL rides on this page are after endDate (we've clearly passed the range)
          const allAfterEndDate = pageRides.every(ride => {
            const rideDate = getRideDate(ride);
            return rideDate !== null && rideDate > endDate;
          });
          
          if (allAfterEndDate) {
            shouldContinueFetching = false;
            console.log(`[DatabaseRidesPanel] Stopping fetch at page ${page + 1} - all rides are after end date`);
          }
        }
      }
      
      const totalRides = allRideEntries.length;
      
      if (totalRides === 0) {
        setRides([]);
        setLoadingStage('idle');
        setLoadingMessage(t('rides.panel.loading.noRidesInRange', { count: pagesFetched, pages: pagesFetched }));
        return;
      }

      // Step 2: Fetch detailed information for filtered rides in batches
      setLoadingStage('fetching-details');
      setLoadingMessage(t('rides.panel.loading.foundInRange', { count: totalRides, value: totalRides, pages: pagesFetched }));
      setLoadingProgress({ current: 0, total: totalRides });
      lastProgressRef.current = 0; // Reset progress tracker
      
      const detailedRides = await fetchRidesDetailedBatch(
        jwtToken,
        selectedProductId,
        allRideEntries.map(r => r.id),
        50, // batch size
        (current, total) => {
          // Ensure progress only increases (prevents jumping backwards)
          // Use the maximum of current reported progress and last known progress
          const safeCurrent = Math.max(current, lastProgressRef.current);
          lastProgressRef.current = safeCurrent;
          
          // Update progress during batch fetch
          setLoadingProgress({ current: safeCurrent, total });
          setLoadingMessage(t('rides.panel.loading.fetchingDetails', { count: total, value: safeCurrent, total }));
        },
        environment
      );
      
      // Ensure final progress is set correctly
      const finalCount = detailedRides?.length || 0;
      setLoadingProgress({ current: finalCount, total: totalRides });
      lastProgressRef.current = finalCount;

      setRides(detailedRides || allRideEntries);
      setLoadingMessage(t('rides.panel.loading.loadedSuccess', { count: detailedRides.length, value: detailedRides.length }));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('rides.panel.loading.fetchRidesError');
      setError(errorMessage);
      setLoadingMessage('');
      console.error('[DatabaseRidesPanel] Error fetching rides:', err);
    } finally {
      setLoading(false);
      setLoadingStage('idle');
      isFetchingRef.current = false;
      // Update last fetched params after fetch completes (success or error)
      // This ensures we can detect if parameters change again
      // Clear progress message after a short delay
      setTimeout(() => {
        setLoadingMessage('');
        setLoadingProgress(null);
      }, 2000);
    }
  }, [jwtToken, selectedProductId, environment, startDate, endDate, downloadAcknowledged, t]);

  // Fetch rides when prerequisites change (use direct dependencies instead of fetchRidesData)
  useEffect(() => {
    // Don't fetch if we're already fetching (prevents concurrent calls)
    if (isFetchingRef.current) {
      console.log('[DatabaseRidesPanel] useEffect: Already fetching, skipping...');
      return;
    }

    // Don't fetch if prerequisites are missing
    if (!jwtToken.trim() || !selectedProductId || !downloadAcknowledged || !startDate || !endDate) {
      console.log('[DatabaseRidesPanel] useEffect: Missing prerequisites, skipping...');
      return;
    }

    // The fetchRidesData function has its own parameter change detection,
    // so we can safely call it here - it will skip if parameters haven't changed
    fetchRidesData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jwtToken, selectedProductId, environment, startDate, endDate, downloadAcknowledged]);

  // Register refresh function (use ref to avoid recreating on every render)
  const fetchRidesDataRef = React.useRef(fetchRidesData);
  useEffect(() => {
    fetchRidesDataRef.current = fetchRidesData;
  }, [fetchRidesData]);

  useEffect(() => {
    if (onRefreshRegister) {
      onRefreshRegister(() => {
        // Force refresh by clearing last fetch params so it will fetch again
        lastFetchParamsRef.current = null;
        isFetchingRef.current = false;
        console.log('[DatabaseRidesPanel] Refresh triggered, forcing new fetch...');
        fetchRidesDataRef.current();
      });
    }
  }, [onRefreshRegister]);

  // Notify parent when rides change (use ref to avoid recreating callback)
  const onRidesChangeRef = React.useRef(onRidesChange);
  useEffect(() => {
    onRidesChangeRef.current = onRidesChange;
  }, [onRidesChange]);

  useEffect(() => {
    if (onRidesChangeRef.current) {
      onRidesChangeRef.current(rides);
    }
  }, [rides]);

  // Get all unique statuses from rides
  const availableStatuses = useMemo(() => {
    const statusSet = new Set<string>();
    rides.forEach(ride => {
      if (ride.status) {
        statusSet.add(ride.status);
      }
    });
    return Array.from(statusSet).sort();
  }, [rides]);

  // Filter rides based on search query and status filter
  const filteredRides = useMemo(() => {
    let filtered = rides;

    // Filter by status
    if (selectedStatuses.length > 0) {
      filtered = filtered.filter(ride => 
        ride.status && selectedStatuses.includes(ride.status)
      );
    }

    // Filter by search query
    if (debouncedSearchQuery.trim()) {
      const query = debouncedSearchQuery.toLowerCase();
      filtered = filtered.filter((ride) => {
        const pickupSegment = ride.segments?.find(s => s.activity === 'pickup');
        const dropoffSegment = ride.segments?.find(s => s.activity === 'dropoff');
        const pickupAddress = pickupSegment?.address?.label?.toLowerCase() || '';
        const dropoffAddress = dropoffSegment?.address?.label?.toLowerCase() || '';
        const rideId = ride.id.toLowerCase();
        const userId = (ride.owner?.id || ride.user?.id || '').toLowerCase();
        const userName = `${ride.owner?.firstName || ''} ${ride.owner?.lastName || ''} ${ride.user?.firstName || ''} ${ride.user?.lastName || ''}`.toLowerCase();

        return pickupAddress.includes(query) ||
               dropoffAddress.includes(query) ||
               rideId.includes(query) ||
               userId.includes(query) ||
               userName.includes(query);
      });
    }

    return filtered;
  }, [rides, debouncedSearchQuery, selectedStatuses]);

  // Update parent with ride counts
  useEffect(() => {
    if (onRideCountChange) {
      onRideCountChange(rides.length, filteredRides.length);
    }
  }, [rides.length, filteredRides.length, onRideCountChange]);

  // Handle selection change
  const handleRideSelect = (rideId: string, selected: boolean) => {
    const newSelection = new Set(selectedRideIds);
    if (selected) {
      newSelection.add(rideId);
    } else {
      newSelection.delete(rideId);
    }
    onSelectionChange(newSelection);
  };

  // Handle select all
  const handleSelectAll = () => {
    const allIds = new Set(filteredRides.map(r => r.id));
    onSelectionChange(allIds);
  };

  // Handle deselect all
  const handleDeselectAll = () => {
    onSelectionChange(new Set());
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box 
        sx={{ 
          p: 2.5, 
          borderBottom: 2, 
          borderColor: 'primary.main',
          flexShrink: 0,
          backgroundColor: 'rgba(25, 118, 210, 0.04)',
        }}
      >
        <Typography 
          variant="h6" 
          sx={{ 
            mb: 1.5,
            fontWeight: 700,
            color: 'primary.main',
            fontSize: '1.1rem',
          }}
        >
          {t('rides.panel.databaseTitle', { env: getEnvironmentDisplayName(environment) })}
        </Typography>
        
        {/* Date Range Pickers */}
        <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Box sx={{ flex: 1, minWidth: '150px', display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <TextField
              label={t('rides.panel.startDateLabel')}
              type="date"
              value={localStartDate}
              onChange={(e) => setLocalStartDate(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              size="small"
              error={!!dateError}
              fullWidth
            />
            <Tooltip
              title={t('rides.panel.startDateTooltipDatabase')}
              arrow
              placement="top"
            >
              <IconButton size="small" sx={{ ml: -1 }}>
                <Icon name="circle-info" size={20} />
              </IconButton>
            </Tooltip>
          </Box>
          <Box sx={{ flex: 1, minWidth: '150px', display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <TextField
              label={t('rides.panel.endDateLabel')}
              type="date"
              value={localEndDate}
              onChange={(e) => setLocalEndDate(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              size="small"
              error={!!dateError}
              fullWidth
            />
            <Tooltip
              title={t('rides.panel.endDateTooltipDatabase')}
              arrow
              placement="top"
            >
              <IconButton size="small" sx={{ ml: -1 }}>
                <Icon name="circle-info" size={20} />
              </IconButton>
            </Tooltip>
          </Box>
          {dateError && (
            <Typography variant="caption" color="error" sx={{ width: '100%' }}>
              {dateError}
            </Typography>
          )}
        </Box>

        {/* Status Filter and Search Bar on same line */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'flex-start' }}>
          {availableStatuses.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 200, flexShrink: 0 }}>
              <InputLabel id="status-filter-label">Filter by Status</InputLabel>
              <Select
                labelId="status-filter-label"
                id="status-filter"
                multiple
                value={selectedStatuses}
                onChange={(e) => setSelectedStatuses(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                input={<OutlinedInput label="Filter by Status" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(selected as string[]).map((value) => {
                      // Get status color info
                      const statusLower = value.toLowerCase();
                      let statusColor = 'default';
                      let statusBgColor = 'rgba(158, 158, 158, 0.1)';
                      let statusTextColor = '#9e9e9e';
                      
                      if (statusLower === 'cancelled' || statusLower === 'canceled') {
                        statusColor = 'error';
                        statusBgColor = 'rgba(211, 47, 47, 0.1)';
                        statusTextColor = '#d32f2f';
                      } else if (statusLower === 'expired') {
                        statusColor = 'warning';
                        statusBgColor = 'rgba(237, 108, 2, 0.1)';
                        statusTextColor = '#ed6c02';
                      } else if (statusLower === 'merged') {
                        statusColor = 'info';
                        statusBgColor = 'rgba(25, 118, 210, 0.1)';
                        statusTextColor = '#1976d2';
                      } else if (statusLower === 'requested' || statusLower === 'pending') {
                        statusColor = 'primary';
                        statusBgColor = 'rgba(25, 118, 210, 0.1)';
                        statusTextColor = '#1976d2';
                      } else if (statusLower === 'confirmed' || statusLower === 'assigned') {
                        statusColor = 'success';
                        statusBgColor = 'rgba(46, 125, 50, 0.1)';
                        statusTextColor = '#2e7d32';
                      }
                      
                      return (
                        <Chip
                          key={value}
                          label={value.charAt(0).toUpperCase() + value.slice(1)}
                          size="small"
                          sx={{
                            backgroundColor: statusBgColor,
                            color: statusTextColor,
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            height: '20px',
                            border: `1px solid ${statusTextColor}40`,
                          }}
                        />
                      );
                    })}
                  </Box>
                )}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                    },
                  },
                }}
              >
                {availableStatuses.map((status) => (
                  <MenuItem key={status} value={status}>
                    <Checkbox checked={selectedStatuses.indexOf(status) > -1} />
                    <ListItemText primary={status.charAt(0).toUpperCase() + status.slice(1)} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Box sx={{ flex: 1 }}>
            <RideSearchBar
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              filteredCount={filteredRides.length}
              totalCount={rides.length}
              placeholder="Search rides by address, ID, or user..."
            />
          </Box>
        </Box>
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            mt: 1.5,
            p: 1,
            backgroundColor: 'rgba(0, 0, 0, 0.02)',
            borderRadius: 1,
          }}
        >
          <Typography 
            variant="body2" 
            sx={{
              color: 'text.secondary',
              fontWeight: 500,
            }}
          >
            {filteredRides.length} of {rides.length} rides
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button 
              size="small" 
              onClick={handleSelectAll} 
              disabled={filteredRides.length === 0 || loading}
              variant="outlined"
              sx={{ 
                textTransform: 'none',
                fontSize: '0.8rem',
              }}
            >
              Select All
            </Button>
            <Button 
              size="small" 
              onClick={handleDeselectAll} 
              disabled={selectedRideIds.size === 0 || loading}
              variant="outlined"
              sx={{ 
                textTransform: 'none',
                fontSize: '0.8rem',
              }}
            >
              Deselect All
            </Button>
          </Box>
        </Box>
        {selectedRideIds.size > 0 && (
          <Box
            sx={{
              mt: 1.5,
              p: 1,
              backgroundColor: 'primary.main',
              color: 'primary.contrastText',
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {selectedRideIds.size} ride{selectedRideIds.size !== 1 ? 's' : ''} selected
            </Typography>
          </Box>
        )}
      </Box>

      {/* Content */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          minHeight: 0,
          p: 2,
          backgroundColor: 'background.default',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'rgba(0, 0, 0, 0.05)',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0, 0, 0, 0.2)',
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
            },
          },
        }}
      >
        {loading ? (
          <Box sx={{ p: 3 }}>
            {/* Loading Header */}
            <Box sx={{ mb: 3, textAlign: 'center' }}>
              <CircularProgress size={40} sx={{ mb: 2 }} />
              <Typography variant="h6" sx={{ mb: 1 }}>
                {loadingStage === 'fetching-list' 
                  ? 'Fetching Rides...' 
                  : loadingStage === 'fetching-details'
                  ? 'Loading Details...'
                  : 'Loading...'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {loadingMessage || 'Please wait while we fetch rides from the database.'}
              </Typography>
              
              {/* Progress Bar */}
              {loadingProgress && (
                <Box sx={{ width: '100%', maxWidth: 400, mx: 'auto' }}>
                  <LinearProgress 
                    variant="determinate" 
                    value={(loadingProgress.current / loadingProgress.total) * 100}
                    sx={{ mb: 1, height: 8, borderRadius: 4 }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {loadingProgress.current} of {loadingProgress.total} ride{loadingProgress.total !== 1 ? 's' : ''}
                    {loadingStage === 'fetching-details' && ` (${Math.round((loadingProgress.current / loadingProgress.total) * 100)}%)`}
                  </Typography>
                </Box>
              )}
              
              {/* Stage-specific info */}
              {loadingStage === 'fetching-list' && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Searching for rides in the selected date range...
                </Typography>
              )}
              {loadingStage === 'fetching-details' && loadingProgress && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Fetching detailed information including addresses and segments...
                </Typography>
              )}
            </Box>
            
            {/* Skeleton Loaders */}
            <Box>
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} variant="rectangular" height={120} sx={{ mb: 1, borderRadius: 1 }} />
              ))}
            </Box>
          </Box>
        ) : error ? (
          <Alert
            severity="error"
            action={
              <Button size="small" onClick={fetchRidesData}>
                Retry
              </Button>
            }
            sx={{ m: 2 }}
          >
            {error}
          </Alert>
        ) : filteredRides.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              {rides.length === 0
                ? 'No rides found in database for the selected date range.'
                : 'No rides match your search criteria.'}
            </Typography>
          </Box>
        ) : (
          filteredRides.map((ride) => (
            <RideCard
              key={ride.id}
              ride={ride}
              source="database"
              selected={selectedRideIds.has(ride.id)}
              onSelect={(selected) => handleRideSelect(ride.id, selected)}
              expanded={expandedRideIds.has(ride.id)}
              onExpand={(expanded) => {
                const newExpanded = new Set(expandedRideIds);
                if (expanded) {
                  newExpanded.add(ride.id);
                } else {
                  newExpanded.delete(ride.id);
                }
                setExpandedRideIds(newExpanded);
              }}
              timezone={effectiveTimezone}
            />
          ))
        )}
      </Box>
    </Box>
  );
};

